import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Platform,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { FormDropdown } from '../components/forms/FormDropdown';
import { Header } from '../components/Header';
import {
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  FileText,
  Filter,
  ImageIcon,
  Paperclip,
  Search,
} from '../components/icons';
import { ListColumnHeader } from '../components/ListColumnHeader';
import { AttachmentCarouselModal } from '../components/modals/AttachmentCarouselModal';
import { userFeedbackCarouselSource } from '../components/modals/AttachmentCarouselModal/userFeedbackCarouselSource';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useRegisterUserFeedbackLeaveGuard } from '../contexts/UserFeedbackLeaveGuardContext';
import { useConfirmationDialog } from '../hooks/useConfirmationDialog';
import { roleService } from '../services/auth/roleService';
import {
  getUserFeedback,
  listUserFeedback,
  saveUserFeedback,
  type UserFeedbackFileMeta,
  type UserFeedbackRecord,
} from '../services/userFeedbackService';
import { colors, spacing } from '../theme';
import { dateFormatting } from '../utils/dateFormatting';
import { REQUEST_LIST_PAGE_SIZES } from '../utils/requestListViewState';
import { showToast } from '../utils/toast';

import type { CarouselMedia } from '../components/modals/AttachmentCarouselModal/carouselMedia';

const COMMENT_MAX = 100000;
/** Minimum height of the read-only feedback box. Longer text grows past this. */
const FEEDBACK_BOX_MIN_HEIGHT = 255;
const SELECT_PLACEHOLDER = 'Select...';
/** Height of a 1,000-character comment in the full-width field. */
const COMMENT_BOX_HEIGHT = 140;

const STATUS_LABEL: Record<string, string> = {
  awaiting_review: 'Awaiting Review',
  under_review: 'Under Review',
  resolved: 'Resolved',
  archived: 'Archived',
};
type PillTone = { backgroundColor: string; color: string };

/**
 * Jira hues with WCAG AA text. Dark text only on a light fill.
 * White text only on a fill dark enough to clear the text.
 */
const NOT_SET_PILL: PillTone = { backgroundColor: '#DFE1E6', color: '#172B4D' };
const STATUS_PILL: Record<string, PillTone> = {
  awaiting_review: { backgroundColor: '#FFD600', color: '#172B4D' },
  under_review: { backgroundColor: '#0052CC', color: '#FFFFFF' },
  resolved: { backgroundColor: '#006B4F', color: '#FFFFFF' },
  archived: { backgroundColor: '#DFE1E6', color: '#172B4D' },
};
const PRIORITY_PILL: Record<string, PillTone> = {
  high: { backgroundColor: '#FFA733', color: '#172B4D' },
  medium: { backgroundColor: '#F5C518', color: '#172B4D' },
  low: { backgroundColor: '#505F79', color: '#FFFFFF' },
};
const PRIORITY_LABEL: Record<string, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};
const CATEGORY_LABEL: Record<string, string> = {
  sign_in: 'Sign-in',
  requests: 'Requests',
  opportunity: 'Opportunity',
  site: 'Site',
  power: 'Power',
  racks: 'Racks',
  cooling: 'Cooling',
  documents_summary: 'Documents and Summary',
  import: 'Import',
  administration: 'Administration',
  other: 'Other',
};
const PLATFORM_LABEL: Record<string, string> = {
  web: 'Web',
  ios: 'iOS',
  android: 'Android',
};

const SORTS: Array<[string, string]> = [
  ['subject', 'Type'],
  ['description', 'Feedback'],
  ['submitter_name', 'Person'],
  ['submitted_at', 'Submitted'],
  ['category', 'Category'],
  ['priority', 'Priority'],
  ['status', 'Status'],
];

type FeedbackNavigate = (
  path: string,
  options?: { replace?: boolean },
) => void | Promise<void>;

interface Props {
  navigate: FeedbackNavigate;
  currentPath?: string;
}

function word(map: Record<string, string>, code: string | null): string {
  if (!code) return 'Not set';
  const match = Object.entries(map).find(([key]) => key === code);
  return match ? match[1] : 'Not set';
}

function codeFor(map: Record<string, string>, label: string): string | null {
  if (label === '' || label === 'Not set') return null;
  const match = Object.entries(map).find(([, text]) => text === label);
  return match ? match[0] : null;
}

function choiceLabel(map: Record<string, string>, code: string | null): string {
  if (!code) return '';
  const match = Object.entries(map).find(([key]) => key === code);
  return match ? match[1] : '';
}

interface FeedbackDraft {
  status: string;
  priority: string;
  category: string;
  comment: string;
}

function isSameDraft(
  record: UserFeedbackRecord,
  draft: FeedbackDraft,
): boolean {
  return (
    draft.status === statusLabel(record.status) &&
    codeFor(PRIORITY_LABEL, draft.priority) === (record.priority ?? null) &&
    codeFor(CATEGORY_LABEL, draft.category) === (record.category ?? null) &&
    draft.comment === (record.adminComments ?? '')
  );
}

function deviceLabel(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return 'Not set';
  if (trimmed.toLowerCase() === 'unknown') return 'Unknown';
  return trimmed;
}

function normalizedComment(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

function statusLabel(code: string | null): string {
  if (code && Object.prototype.hasOwnProperty.call(STATUS_LABEL, code)) {
    return STATUS_LABEL[code];
  }
  return STATUS_LABEL.awaiting_review;
}

function statusCode(label: string): string {
  const match = Object.entries(STATUS_LABEL).find(([, text]) => text === label);
  return match ? match[0] : 'awaiting_review';
}

function statusTone(code: string | null): PillTone {
  if (code && STATUS_PILL[code]) return STATUS_PILL[code];
  return STATUS_PILL.awaiting_review;
}

function fileKind(file: UserFeedbackFileMeta): string {
  const slash = file.mimeType.lastIndexOf('/');
  const subtype = slash >= 0 ? file.mimeType.slice(slash + 1) : '';
  if (subtype === 'jpeg') return 'JPG';
  if (subtype && subtype !== 'octet-stream' && !subtype.includes('.')) {
    return subtype.toUpperCase();
  }
  const dot = file.fileName.lastIndexOf('.');
  const fromName = dot > 0 ? file.fileName.slice(dot + 1).toUpperCase() : '';
  return fromName || 'FILE';
}

function submittedLabel(value: string): string {
  return (
    dateFormatting.format(value, {
      locale: 'en-US',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) ?? value
  );
}

function feedbackIdFromPath(path: string | undefined): number | null {
  const match = /(?:^|[?&])id=(\d+)/.exec(path ?? '');
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

const LIST_DEFAULT_SORT = 'submitted_at';
const LIST_DEFAULT_DIRECTION = 'DESC' as const;
const SORT_KEYS = new Set(SORTS.map(([key]) => key));
const PAGE_SIZE_SET = new Set<number>(REQUEST_LIST_PAGE_SIZES);

interface ListQuery {
  q: string;
  status: string;
  priority: string;
  category: string;
  feedbackType: string;
  orderBy: string;
  orderDirection: 'ASC' | 'DESC';
  page: number;
  pageSize: number;
}

function listQueryFromPath(path: string | undefined): ListQuery {
  const query = path?.includes('?') ? path.slice(path.indexOf('?') + 1) : '';
  const params = new URLSearchParams(query);
  const sort = params.get('sort') ?? '';
  const dir = params.get('dir');
  const page = Number(params.get('page'));
  const size = Number(params.get('size'));
  return {
    q: params.get('q') ?? '',
    status: params.get('status') ?? '',
    priority: params.get('priority') ?? '',
    category: params.get('category') ?? '',
    feedbackType: params.get('type') ?? '',
    orderBy: SORT_KEYS.has(sort) ? sort : LIST_DEFAULT_SORT,
    orderDirection:
      dir === 'ASC' || dir === 'DESC' ? dir : LIST_DEFAULT_DIRECTION,
    page: Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: PAGE_SIZE_SET.has(size) ? size : 20,
  };
}

function listPath(query: ListQuery): string {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.status) params.set('status', query.status);
  if (query.priority) params.set('priority', query.priority);
  if (query.category) params.set('category', query.category);
  if (query.feedbackType) params.set('type', query.feedbackType);
  if (query.orderBy !== LIST_DEFAULT_SORT) params.set('sort', query.orderBy);
  if (query.orderDirection !== LIST_DEFAULT_DIRECTION)
    params.set('dir', query.orderDirection);
  if (query.page !== 1) params.set('page', String(query.page));
  if (query.pageSize !== 20) params.set('size', String(query.pageSize));
  const text = params.toString();
  return text ? `/user-feedback?${text}` : '/user-feedback';
}

function detailPath(feedbackId: number, query: ListQuery): string {
  const params = new URLSearchParams(listPath(query).split('?')[1] ?? '');
  params.set('id', String(feedbackId));
  return `/user-feedback?${params.toString()}`;
}

function rememberListUrl(query: ListQuery): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const path = listPath(query);
  const current = `${window.location.pathname}${window.location.search}`;
  if (current === path) return;
  window.history.replaceState(window.history.state, '', path);
}

function toneFor(
  map: Record<string, string>,
  label: string,
  tones: Record<string, PillTone>,
): PillTone {
  const code = codeFor(map, label);
  if (!code) return NOT_SET_PILL;
  return tones[code] ?? NOT_SET_PILL;
}

const ValuePill: React.FC<{ label: string; tone: PillTone }> = ({
  label,
  tone,
}) => (
  <Text style={[styles.pill, tone]} numberOfLines={1}>
    {label}
  </Text>
);

const SortHeader: React.FC<{
  label: string;
  sortKey: string;
  activeKey: string;
  direction: 'ASC' | 'DESC';
  onSort: (key: string) => void;
}> = ({ label, sortKey, activeKey, direction, onSort }) => {
  const active = activeKey === sortKey;
  const Icon = active
    ? direction === 'ASC'
      ? ChevronUp
      : ChevronDown
    : ArrowUpDown;
  return (
    <TouchableOpacity
      style={styles.sortHeader}
      onPress={() => onSort(sortKey)}
      accessibilityRole="button"
      accessibilityLabel={`Sort by ${label}`}
    >
      <Text style={styles.sortLabel} numberOfLines={1}>
        {label}
      </Text>
      <View style={styles.sortIcon}>
        <Icon size={14} color={active ? colors.primary : '#888'} />
      </View>
    </TouchableOpacity>
  );
};

export const UserFeedback: React.FC<Props> = ({ navigate, currentPath }) => {
  const { user } = useAuth();
  const { isImpersonating, mode } = useImpersonation();
  const readOnly = isImpersonating && mode === 'read_only';
  const allowed =
    typeof roleService.canAccessUserFeedback === 'function' &&
    roleService.canAccessUserFeedback(user);
  const openId = feedbackIdFromPath(currentPath);

  useEffect(() => {
    if (user && !allowed) navigate('/unauthorized');
  }, [user, allowed, navigate]);

  if (!allowed) return null;
  return openId === null ? (
    <FeedbackList navigate={navigate} currentPath={currentPath} />
  ) : (
    <FeedbackDetail
      navigate={navigate}
      currentPath={currentPath}
      feedbackId={openId}
      readOnly={readOnly}
    />
  );
};

const FeedbackList: React.FC<{
  navigate: FeedbackNavigate;
  currentPath?: string;
}> = ({ navigate, currentPath }) => {
  const initialQuery = listQueryFromPath(currentPath);
  const [searchText, setSearchText] = useState(initialQuery.q);
  const [debouncedSearch, setDebouncedSearch] = useState(initialQuery.q.trim());
  const [status, setStatus] = useState(initialQuery.status);
  const [priority, setPriority] = useState(initialQuery.priority);
  const [category, setCategory] = useState(initialQuery.category);
  const [feedbackType, setFeedbackType] = useState(initialQuery.feedbackType);
  const [orderBy, setOrderBy] = useState(initialQuery.orderBy);
  const [orderDirection, setOrderDirection] = useState<'ASC' | 'DESC'>(
    initialQuery.orderDirection,
  );
  const [page, setPage] = useState(initialQuery.page);
  const [pageSize, setPageSize] = useState(initialQuery.pageSize);
  const [showPageSize, setShowPageSize] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [rows, setRows] = useState<UserFeedbackRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState<{
    feedbackId: number;
    files: UserFeedbackFileMeta[];
  } | null>(null);

  const debouncedSearchRef = useRef(debouncedSearch);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchText.trim();
      if (next === debouncedSearchRef.current) return;
      debouncedSearchRef.current = next;
      setDebouncedSearch(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const currentQuery = useCallback(
    (): ListQuery => ({
      q: searchText.trim(),
      status,
      priority,
      category,
      feedbackType,
      orderBy,
      orderDirection,
      page,
      pageSize,
    }),
    [
      searchText,
      status,
      priority,
      category,
      feedbackType,
      orderBy,
      orderDirection,
      page,
      pageSize,
    ],
  );

  useEffect(() => {
    const query: ListQuery = {
      q: debouncedSearch,
      status,
      priority,
      category,
      feedbackType,
      orderBy,
      orderDirection,
      page,
      pageSize,
    };
    rememberListUrl(query);
    const path = listPath(query);
    if (path !== currentPath) {
      void navigateRef.current(path, { replace: true });
    }
  }, [
    debouncedSearch,
    status,
    priority,
    category,
    feedbackType,
    orderBy,
    orderDirection,
    page,
    pageSize,
    currentPath,
  ]);

  const load = useCallback(() => {
    let active = true;
    setLoading(true);
    setError('');
    listUserFeedback({
      page,
      pageSize,
      orderBy,
      orderDirection,
      searchText: debouncedSearch || undefined,
      status: status || undefined,
      priority: priority || undefined,
      category: category || undefined,
      feedbackType: feedbackType || undefined,
    })
      .then(result => {
        if (!active) return;
        const lastPage = result.pagination.totalPages;
        if (lastPage > 0 && page > lastPage) {
          setPage(lastPage);
          return;
        }
        setRows(result.data);
        setTotal(result.pagination.total);
        setTotalPages(lastPage);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
        setError('Unable to load user feedback');
      });
    return () => {
      active = false;
    };
  }, [
    page,
    pageSize,
    orderBy,
    orderDirection,
    debouncedSearch,
    status,
    priority,
    category,
    feedbackType,
  ]);

  useEffect(() => load(), [load]);

  const emptyMessage =
    debouncedSearch || status || priority || category || feedbackType
      ? 'No feedback matches these filters.'
      : 'No user feedback yet.';

  const headerStyle = (key: string): StyleProp<ViewStyle> => {
    if (key === 'subject') return styles.typeCol;
    if (key === 'description') return styles.feedbackCol;
    if (key === 'submitter_name') return styles.personCol;
    if (key === 'submitted_at') return styles.dateCol;
    if (key === 'status') return styles.statusCol;
    return styles.shortCol;
  };

  const filtersApplied = Boolean(
    status || priority || category || feedbackType,
  );
  const filterHighlighted = showFilter || filtersApplied;

  const openRow = (feedbackId: number) => {
    const query = currentQuery();
    rememberListUrl(query);
    navigate(detailPath(feedbackId, query));
  };

  const chooseSort = (key: string) => {
    if (key === orderBy) {
      setOrderDirection(current => (current === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setOrderBy(key);
      setOrderDirection(key === 'submitted_at' ? 'DESC' : 'ASC');
    }
    setPage(1);
  };

  return (
    <View style={styles.page}>
      <Header navigate={navigate} currentPath={currentPath} />
      <ScrollView style={styles.listScroll} contentContainerStyle={styles.body}>
        <Text style={styles.title}>User Feedback</Text>
        <View style={styles.toolbar}>
          <View style={styles.searchBox}>
            <Search size={16} color={colors.textSecondary} />
            <TextInput
              value={searchText}
              onChangeText={setSearchText}
              placeholder="Search feedback..."
              placeholderTextColor={colors.textSecondary}
              style={styles.searchInput}
              testID="user-feedback-search"
            />
          </View>
          <View style={styles.toolGroup}>
            <View style={styles.menuAnchor}>
              <TouchableOpacity
                style={[
                  styles.menuButton,
                  filterHighlighted && styles.menuButtonActive,
                ]}
                onPress={() => setShowFilter(open => !open)}
                testID="user-feedback-filter"
                accessibilityLabel={
                  filtersApplied ? 'Filter, filters applied' : 'Filter'
                }
              >
                <Filter
                  size={14}
                  color={filterHighlighted ? colors.primary : '#666'}
                />
                <Text
                  style={[
                    styles.menuButtonText,
                    filterHighlighted && styles.menuButtonTextActive,
                  ]}
                >
                  Filter
                </Text>
              </TouchableOpacity>
              {showFilter ? (
                <FilterMenu
                  status={status}
                  priority={priority}
                  category={category}
                  feedbackType={feedbackType}
                  onChange={(key, value) => {
                    if (key === 'status') setStatus(value);
                    if (key === 'priority') setPriority(value);
                    if (key === 'category') setCategory(value);
                    if (key === 'feedbackType') setFeedbackType(value);
                    setPage(1);
                  }}
                  onClear={() => {
                    setStatus('');
                    setPriority('');
                    setCategory('');
                    setFeedbackType('');
                    setPage(1);
                  }}
                />
              ) : null}
            </View>
          </View>
        </View>

        <View style={styles.table}>
          <ListColumnHeader style={styles.headerBar} columnGap={spacing.md}>
            {SORTS.map(([key, label]) => (
              <View
                key={key}
                style={headerStyle(key)}
                testID={`user-feedback-header-${key}`}
              >
                <SortHeader
                  label={label}
                  sortKey={key}
                  activeKey={orderBy}
                  direction={orderDirection}
                  onSort={chooseSort}
                />
              </View>
            ))}
            <View style={styles.clipCol} />
          </ListColumnHeader>
          {loading ? (
            <View style={styles.messageRow}>
              <ActivityIndicator color={colors.primary} />
              <Text style={styles.message}>Loading feedback...</Text>
            </View>
          ) : error ? (
            <View style={styles.messageRow}>
              <Text style={styles.message}>{error}</Text>
              <TouchableOpacity onPress={load} testID="user-feedback-retry">
                <Text style={styles.retry}>Try again</Text>
              </TouchableOpacity>
            </View>
          ) : rows.length === 0 ? (
            <Text style={styles.message}>{emptyMessage}</Text>
          ) : (
            rows.map(row => (
              <TouchableOpacity
                key={row.feedbackId}
                style={styles.row}
                onPress={() => openRow(row.feedbackId)}
                testID={`user-feedback-row-${row.feedbackId}`}
              >
                <View style={styles.typeCol}>
                  <Text style={styles.idText}>{row.feedbackId}</Text>
                  <Text style={styles.typeText} numberOfLines={1}>
                    {row.subject}
                  </Text>
                </View>
                <View
                  style={styles.feedbackCol}
                  testID={`user-feedback-cell-description-${row.feedbackId}`}
                >
                  <Text style={styles.cell} numberOfLines={2}>
                    {row.description}
                  </Text>
                </View>
                <View
                  style={styles.personCol}
                  testID={`user-feedback-cell-person-${row.feedbackId}`}
                >
                  <Text style={styles.cell} numberOfLines={1}>
                    {row.submitterName}
                  </Text>
                </View>
                <View
                  style={styles.dateCol}
                  testID={`user-feedback-cell-submitted-${row.feedbackId}`}
                >
                  <Text style={styles.cell} numberOfLines={1}>
                    {submittedLabel(row.submittedAt)}
                  </Text>
                </View>
                <View style={styles.shortCol}>
                  <Text style={styles.cell} numberOfLines={1}>
                    {word(CATEGORY_LABEL, row.category)}
                  </Text>
                </View>
                <View style={styles.shortCol}>
                  <ValuePill
                    label={word(PRIORITY_LABEL, row.priority)}
                    tone={toneFor(
                      PRIORITY_LABEL,
                      word(PRIORITY_LABEL, row.priority),
                      PRIORITY_PILL,
                    )}
                  />
                </View>
                <View style={styles.statusCol}>
                  <ValuePill
                    label={statusLabel(row.status)}
                    tone={statusTone(row.status)}
                  />
                </View>
                <View style={styles.clipCol}>
                  {row.attachmentCount > 0 ? (
                    <TouchableOpacity
                      onPress={() =>
                        setViewer({
                          feedbackId: row.feedbackId,
                          files: row.files,
                        })
                      }
                      accessibilityLabel={`Open files for ${row.feedbackId}`}
                      testID={`user-feedback-file-${row.feedbackId}`}
                    >
                      <Paperclip size={16} color="#666" />
                    </TouchableOpacity>
                  ) : null}
                </View>
              </TouchableOpacity>
            ))
          )}
        </View>
        {!loading && !error && total > 0 ? (
          <View style={styles.paginationContainer}>
            <Text style={styles.paginationText}>
              {`Showing ${(page - 1) * pageSize + 1}-${Math.min(
                page * pageSize,
                total,
              )} of ${total} feedback`}
            </Text>
            <View style={styles.paginationControls}>
              <Text style={styles.paginationLabel}>Rows per page:</Text>
              <View style={styles.pageSizeContainer}>
                <TouchableOpacity
                  style={styles.pageSizeDropdown}
                  onPress={() => setShowPageSize(open => !open)}
                  testID="user-feedback-page-size"
                >
                  <Text style={styles.pageSizeText}>{pageSize}</Text>
                  <View
                    style={{
                      marginLeft: 6,
                      transform: [{ rotate: showPageSize ? '180deg' : '0deg' }],
                    }}
                  >
                    <ChevronDown size={10} color="#666" />
                  </View>
                </TouchableOpacity>
                {showPageSize ? (
                  <View style={styles.pageSizeOptions}>
                    {REQUEST_LIST_PAGE_SIZES.map(size => (
                      <TouchableOpacity
                        key={size}
                        style={[
                          styles.pageSizeOption,
                          pageSize === size && styles.pageSizeOptionActive,
                        ]}
                        onPress={() => {
                          setPageSize(size);
                          setPage(1);
                          setShowPageSize(false);
                        }}
                      >
                        <Text
                          style={[
                            styles.pageSizeOptionText,
                            pageSize === size &&
                              styles.pageSizeOptionTextActive,
                          ]}
                        >
                          {size}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                ) : null}
              </View>
              <TouchableOpacity
                style={[
                  styles.pageButton,
                  page <= 1 && styles.pageButtonDisabled,
                ]}
                onPress={() => setPage(current => Math.max(1, current - 1))}
                disabled={page <= 1}
                testID="user-feedback-previous"
              >
                <Text
                  style={[
                    styles.pageButtonText,
                    page <= 1 && styles.pageButtonTextDisabled,
                  ]}
                >
                  Previous
                </Text>
              </TouchableOpacity>
              <Text style={styles.pageInfo}>
                {`Page ${page} of ${Math.max(totalPages, 1)}`}
              </Text>
              <TouchableOpacity
                style={[
                  styles.pageButton,
                  page >= totalPages && styles.pageButtonDisabled,
                ]}
                onPress={() =>
                  setPage(current => Math.min(totalPages, current + 1))
                }
                disabled={page >= totalPages}
                testID="user-feedback-next"
              >
                <Text
                  style={[
                    styles.pageButtonText,
                    page >= totalPages && styles.pageButtonTextDisabled,
                  ]}
                >
                  Next
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}
      </ScrollView>
      <FileViewer
        feedbackId={viewer?.feedbackId ?? null}
        files={viewer?.files ?? []}
        onClose={() => setViewer(null)}
      />
    </View>
  );
};

const FilterMenu: React.FC<{
  status: string;
  priority: string;
  category: string;
  feedbackType: string;
  onChange: (
    key: 'status' | 'priority' | 'category' | 'feedbackType',
    value: string,
  ) => void;
  onClear: () => void;
}> = ({ status, priority, category, feedbackType, onChange, onClear }) => {
  const sections: Array<{
    key: 'status' | 'priority' | 'category' | 'feedbackType';
    label: string;
    value: string;
    choices: Array<[string, string]>;
  }> = [
    {
      key: 'status',
      label: 'Status',
      value: status,
      choices: [['', 'All'], ...Object.entries(STATUS_LABEL)],
    },
    {
      key: 'priority',
      label: 'Priority',
      value: priority,
      choices: [
        ['', 'All'],
        ['not_set', 'Not set'],
        ...Object.entries(PRIORITY_LABEL),
      ],
    },
    {
      key: 'category',
      label: 'Category',
      value: category,
      choices: [
        ['', 'All'],
        ['not_set', 'Not set'],
        ...Object.entries(CATEGORY_LABEL),
      ],
    },
    {
      key: 'feedbackType',
      label: 'Type',
      value: feedbackType,
      choices: [
        ['', 'All'],
        ['Bug', 'Bug'],
        ['Enhancement', 'Enhancement'],
      ],
    },
  ];
  return (
    <View style={styles.panel}>
      <View style={styles.menuHead}>
        <Text style={styles.menuTitle}>Filter Options</Text>
        <TouchableOpacity onPress={onClear}>
          <Text style={styles.menuAction}>Clear All</Text>
        </TouchableOpacity>
      </View>
      <ScrollView style={styles.menuScroll}>
        {sections.map(section => (
          <View key={section.key} style={styles.section}>
            <Text style={styles.sectionLabel}>{section.label}</Text>
            {section.choices.map(([value, label]) => {
              const selected = section.value === value;
              return (
                <TouchableOpacity
                  key={`${section.key}-${value || 'all'}`}
                  style={[styles.choice, selected && styles.choiceSelected]}
                  onPress={() => onChange(section.key, value)}
                >
                  <Text
                    style={[
                      styles.choiceText,
                      selected && styles.choiceTextSelected,
                    ]}
                  >
                    {label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

const FeedbackDetail: React.FC<{
  navigate: FeedbackNavigate;
  currentPath?: string;
  feedbackId: number;
  readOnly: boolean;
}> = ({ navigate, currentPath, feedbackId, readOnly }) => {
  const [row, setRow] = useState<UserFeedbackRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(STATUS_LABEL.awaiting_review);
  const [priority, setPriority] = useState('');
  const [category, setCategory] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const rowRef = useRef(row);
  const draftRef = useRef<FeedbackDraft>({
    status,
    priority,
    category,
    comment,
  });
  const savingRef = useRef(false);
  const saveToken = useRef(0);
  const leaveConfirmedRef = useRef(false);
  rowRef.current = row;
  draftRef.current = { status, priority, category, comment };
  const { confirm, confirmationDialog } = useConfirmationDialog();

  const applyRecord = useCallback((result: UserFeedbackRecord) => {
    const next: FeedbackDraft = {
      status: statusLabel(result.status),
      priority: choiceLabel(PRIORITY_LABEL, result.priority),
      category: choiceLabel(CATEGORY_LABEL, result.category),
      comment: result.adminComments ?? '',
    };
    rowRef.current = result;
    draftRef.current = next;
    setRow(result);
    setStatus(next.status);
    setPriority(next.priority);
    setCategory(next.category);
    setComment(next.comment);
  }, []);

  const load = useCallback(() => {
    let active = true;
    setLoading(true);
    setError('');
    getUserFeedback(feedbackId)
      .then(result => {
        if (!active) return;
        applyRecord(result);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
        setError('Unable to load user feedback');
      });
    return () => {
      active = false;
    };
  }, [applyRecord, feedbackId]);

  useEffect(() => load(), [load]);

  const confirmLeave = useCallback(async () => {
    const current = rowRef.current;
    if (!current || isSameDraft(current, draftRef.current)) return true;
    const discard = await confirm({
      title: 'Discard unsaved changes?',
      message: 'You have unsaved changes that will be lost.',
      confirmText: 'Discard',
      cancelText: 'Stay',
      confirmButtonStyle: 'danger',
      showWarningIcon: true,
    });
    if (discard && savingRef.current) {
      saveToken.current += 1;
      savingRef.current = false;
      setSaving(false);
    }
    return discard;
  }, [confirm]);

  const leaveTo = useCallback(
    async (path: string) => {
      const mayLeave = await confirmLeave();
      if (!mayLeave) return;
      leaveConfirmedRef.current = true;
      try {
        await navigate(path, { replace: true });
      } finally {
        leaveConfirmedRef.current = false;
      }
    },
    [confirmLeave, navigate],
  );

  useRegisterUserFeedbackLeaveGuard(
    useCallback(async () => {
      if (leaveConfirmedRef.current) {
        leaveConfirmedRef.current = false;
        return true;
      }
      return confirmLeave();
    }, [confirmLeave]),
  );

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined')
      return undefined;
    const onLeave = (event: BeforeUnloadEvent) => {
      const current = rowRef.current;
      if (!current || isSameDraft(current, draftRef.current)) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, []);

  const save = () => {
    const current = rowRef.current;
    const draft = draftRef.current;
    if (!current || savingRef.current) return;
    if (isSameDraft(current, draft)) {
      showToast.info('No changes to save');
      return;
    }
    const token = ++saveToken.current;
    const revertTo = {
      status: current.status ?? 'awaiting_review',
      priority: current.priority,
      category: current.category,
      adminComments: normalizedComment(current.adminComments),
    };
    savingRef.current = true;
    setSaving(true);
    setSaveError('');
    const loadingToast = showToast.loading('Saving changes...');
    saveUserFeedback(feedbackId, {
      status: statusCode(draft.status),
      priority: codeFor(PRIORITY_LABEL, draft.priority),
      category: codeFor(CATEGORY_LABEL, draft.category),
      adminComments: normalizedComment(draft.comment),
    })
      .then(result => {
        if (token !== saveToken.current) {
          showToast.dismiss(loadingToast);
          const undo = {
            ...revertTo,
            ...(typeof result.updatedAt === 'string' && result.updatedAt !== ''
              ? { expectedUpdatedAt: result.updatedAt }
              : {}),
          };
          void saveUserFeedback(feedbackId, undo).catch(() => {
            showToast.error(
              'Could not undo the save. Open the report again to see what is stored.',
            );
            applyRecord(result);
          });
          return;
        }
        applyRecord(result);
        savingRef.current = false;
        setSaving(false);
        showToast.dismiss(loadingToast);
        showToast.success('Feedback saved successfully!');
      })
      .catch(() => {
        if (token !== saveToken.current) return;
        savingRef.current = false;
        setSaving(false);
        showToast.dismiss(loadingToast);
        const message = 'Failed to save changes. Please try again.';
        setSaveError(message);
        showToast.error(message);
      });
  };

  const returnPath = listPath(listQueryFromPath(currentPath));

  return (
    <View style={styles.page}>
      <Header navigate={navigate} currentPath={currentPath} />
      <ScrollView
        style={styles.detailScroll}
        contentContainerStyle={styles.body}
      >
        <TouchableOpacity
          onPress={() => leaveTo(returnPath)}
          testID="user-feedback-back"
        >
          <Text style={styles.back}>‹ User Feedback</Text>
        </TouchableOpacity>
        {loading ? (
          <Text style={styles.message}>Loading feedback...</Text>
        ) : error || !row ? (
          <View>
            <Text style={styles.message}>
              {error || 'Unable to load user feedback'}
            </Text>
            <TouchableOpacity onPress={load}>
              <Text style={styles.retry}>Try again</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.card}>
            <Text style={styles.detailTitle}>
              #{row.feedbackId} · Submitted by {row.submitterName}
            </Text>
            <View style={styles.meta} testID="user-feedback-meta">
              <Meta label="Type" value={row.subject} />
              <Meta label="Submitted" value={submittedLabel(row.submittedAt)} />
              <Meta
                label="Platform"
                value={PLATFORM_LABEL[row.platform] ?? row.platform}
              />
              <Meta label="Device" value={deviceLabel(row.deviceModel)} />
            </View>
            <View style={styles.pair}>
              <View style={styles.pairItem}>
                <FormDropdown
                  label="Category"
                  value={category}
                  options={Object.values(CATEGORY_LABEL)}
                  placeholder={SELECT_PLACEHOLDER}
                  onSelect={value => {
                    if (typeof value !== 'string') return;
                    setCategory(value);
                  }}
                  allowClear
                  disabled={readOnly || saving}
                  testID="user-feedback-category"
                />
              </View>
              <View style={styles.pairItem} />
            </View>
            <View style={styles.pair}>
              <View style={styles.pairItem}>
                <FormDropdown
                  label="Priority"
                  value={priority}
                  options={Object.values(PRIORITY_LABEL)}
                  placeholder={SELECT_PLACEHOLDER}
                  onSelect={value => {
                    if (typeof value !== 'string') return;
                    setPriority(value);
                  }}
                  allowClear
                  disabled={readOnly || saving}
                  renderSelectedValue={selected => (
                    <ValuePill
                      label={selected}
                      tone={toneFor(PRIORITY_LABEL, selected, PRIORITY_PILL)}
                    />
                  )}
                  testID="user-feedback-priority"
                />
              </View>
              <View style={styles.pairItem}>
                <FormDropdown
                  label="Status"
                  value={status}
                  options={Object.values(STATUS_LABEL)}
                  onSelect={value => {
                    if (typeof value !== 'string' || value === '') return;
                    setStatus(value);
                  }}
                  disabled={readOnly || saving}
                  renderSelectedValue={selected => (
                    <ValuePill
                      label={selected}
                      tone={toneFor(STATUS_LABEL, selected, STATUS_PILL)}
                    />
                  )}
                  testID="user-feedback-status"
                />
              </View>
            </View>
            <View style={styles.pair}>
              <View style={styles.pairItem}>
                <Text style={styles.fieldLabel}>Feedback</Text>
                <View style={styles.readonlyBox} testID="user-feedback-body">
                  <Text style={styles.feedbackValue}>{row.description}</Text>
                </View>
              </View>
              <View style={styles.pairItem}>
                <Text style={styles.fieldLabel}>Attachment</Text>
                {row.files.length === 0 ? (
                  <Text style={styles.message}>No files uploaded</Text>
                ) : (
                  <View style={styles.fileList}>
                    {row.files.map((file, index) => {
                      const image = file.mimeType.startsWith('image/');
                      const KindIcon = image ? ImageIcon : FileText;
                      return (
                        <TouchableOpacity
                          key={file.fileId}
                          style={styles.fileTile}
                          onPress={() => setViewerIndex(index)}
                          accessibilityLabel={`Open ${file.fileName}`}
                          testID={`user-feedback-attachment-${file.fileId}`}
                        >
                          <View style={styles.filePreview}>
                            <View style={styles.fileMark}>
                              <KindIcon size={28} color="#9CA3AF" />
                              <View style={styles.fileBadge}>
                                <Text style={styles.fileBadgeText}>
                                  {fileKind(file)}
                                </Text>
                              </View>
                            </View>
                          </View>
                          <Text style={styles.fileName} numberOfLines={2}>
                            {file.fileName}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                )}
              </View>
            </View>
            <Text style={styles.fieldLabel}>Comment</Text>
            <TextInput
              value={comment}
              onChangeText={text => setComment(text.slice(0, COMMENT_MAX))}
              editable={!readOnly && !saving}
              multiline
              scrollEnabled
              style={styles.comment}
              testID="user-feedback-comment"
            />
            <Text style={styles.counter}>
              {comment.length} / {COMMENT_MAX}
            </Text>
            <FileViewer
              feedbackId={viewerIndex === null ? null : row.feedbackId}
              files={row.files}
              initialIndex={viewerIndex ?? 0}
              onClose={() => setViewerIndex(null)}
            />
          </View>
        )}
      </ScrollView>
      {!loading && row ? (
        <View style={styles.footer} testID="user-feedback-footer">
          <View style={styles.footerContent}>
            <Text style={styles.saveError}>{saveError}</Text>
            <View style={styles.footerActions}>
              <TouchableOpacity
                style={styles.cancel}
                onPress={() => leaveTo(returnPath)}
                accessibilityRole="button"
                testID="user-feedback-cancel"
              >
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              {readOnly ? null : (
                <TouchableOpacity
                  style={styles.save}
                  onPress={save}
                  disabled={saving}
                  accessibilityRole="button"
                  testID="user-feedback-save"
                >
                  <Text style={styles.saveText}>
                    {saving ? 'Saving...' : 'Save'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      ) : null}
      {confirmationDialog}
    </View>
  );
};

const Meta: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <View style={styles.metaItem}>
    <Text style={styles.metaLabel}>{label}</Text>
    <Text style={styles.metaValue} numberOfLines={1}>
      {value}
    </Text>
  </View>
);

const FileViewer: React.FC<{
  feedbackId: number | null;
  files: UserFeedbackFileMeta[];
  initialIndex?: number;
  onClose: () => void;
}> = ({ feedbackId, files, initialIndex = 0, onClose }) => {
  const items = useMemo<CarouselMedia[]>(
    () =>
      files.map(file => ({
        id: String(file.fileId),
        name: file.fileName,
        mimeType: file.mimeType,
        sizeBytes: file.fileSizeBytes,
      })),
    [files],
  );
  if (feedbackId === null) return null;
  return (
    <AttachmentCarouselModal
      visible
      onClose={onClose}
      items={items}
      initialIndex={initialIndex}
      sectionLabel="User Feedback"
      source={userFeedbackCarouselSource(feedbackId)}
    />
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: '#F5F6F8' },
  listScroll: { flex: 1, width: '100%' },
  body: {
    padding: 24,
    gap: 16,
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
  },
  title: { color: colors.primary, fontSize: 22, fontWeight: '600' },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    zIndex: 5,
    width: '100%',
  },
  toolGroup: {
    marginLeft: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  searchBox: {
    flex: 1,
    maxWidth: 360,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 40,
  },
  searchInput: { flex: 1, color: '#333', paddingVertical: 0 },
  menuAnchor: { position: 'relative' },
  menuButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingHorizontal: 14,
    height: 40,
  },
  menuButtonActive: { borderColor: colors.primary },
  menuButtonText: { color: '#333', fontSize: 14 },
  menuButtonTextActive: { color: colors.primary },
  panel: {
    position: 'absolute',
    top: 48,
    right: 0,
    width: 280,
    maxHeight: 420,
    backgroundColor: '#fff',
    borderRadius: 8,
    zIndex: 20,
    shadowColor: '#000',
    shadowOpacity: 0.16,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 4 },
    elevation: 8,
  },
  menuHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E9ECEF',
  },
  menuTitle: { fontSize: 16, fontWeight: '600', color: '#333' },
  menuAction: { color: colors.primary, fontSize: 14, fontWeight: '500' },
  menuScroll: { maxHeight: 340 },
  section: { paddingVertical: 8 },
  sectionLabel: {
    paddingHorizontal: 16,
    paddingBottom: 4,
    fontSize: 11,
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
  },
  choice: { paddingHorizontal: 16, paddingVertical: 8 },
  choiceSelected: { backgroundColor: colors.surfaceAccentMuted },
  choiceText: { color: '#333', fontSize: 14 },
  choiceTextSelected: { color: colors.primary, fontWeight: '500' },
  sortHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    width: '100%',
    minWidth: 0,
  },
  sortIcon: { flexShrink: 0 },
  sortLabel: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  table: { width: '100%' },
  headerBar: { width: '100%' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginBottom: 8,
    width: '100%',
    gap: spacing.md,
  },
  typeCol: {
    flexGrow: 0.8,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 108,
    overflow: 'hidden',
  },
  feedbackCol: {
    flexGrow: 2.4,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  personCol: {
    flexGrow: 1.2,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  dateCol: {
    flexGrow: 1.1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  shortCol: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  statusCol: {
    flexGrow: 1.1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 136,
    overflow: 'hidden',
  },
  clipCol: {
    width: 36,
    flexGrow: 0,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  idText: { color: '#666', fontSize: 12 },
  typeText: { color: '#111', fontSize: 14, fontWeight: '600' },
  cell: { color: '#333', fontSize: 14, overflow: 'hidden' },
  pill: {
    color: '#fff',
    overflow: 'hidden',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    fontSize: 12,
    fontWeight: '600',
    alignSelf: 'flex-start',
  },
  messageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 16,
  },
  message: { color: '#666', fontSize: 14 },
  retry: { color: colors.primary, fontSize: 14, fontWeight: '600' },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#ddd',
    width: '100%',
    maxWidth: 1200,
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  paginationLabel: { fontSize: 14, color: '#666' },
  paginationText: { fontSize: 14, color: '#666' },
  pageButton: {
    backgroundColor: '#fff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  pageButtonDisabled: { opacity: 0.5 },
  pageButtonText: { fontSize: 14, color: '#333', fontWeight: '500' },
  pageButtonTextDisabled: { color: '#999' },
  pageInfo: { fontSize: 14, color: '#666', marginHorizontal: 8 },
  pageSizeContainer: { position: 'relative', zIndex: 2 },
  pageSizeDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  pageSizeText: { fontSize: 14, color: '#333', fontWeight: '500' },
  pageSizeOptions: {
    position: 'absolute',
    bottom: 36,
    right: 0,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    minWidth: 80,
    zIndex: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 8,
  },
  pageSizeOption: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    minHeight: 44,
    justifyContent: 'center',
  },
  pageSizeOptionActive: { backgroundColor: '#fff8f4' },
  pageSizeOptionText: { fontSize: 14, color: '#333', textAlign: 'center' },
  pageSizeOptionTextActive: { color: colors.primary, fontWeight: '500' },
  back: {
    color: colors.primary,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    gap: 12,
  },
  detailTitle: { fontSize: 16, fontWeight: '600', color: '#111' },
  meta: {
    flexDirection: 'row',
    backgroundColor: '#F8F9FB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 12,
  },
  metaItem: { flex: 1 },
  metaLabel: {
    color: '#888',
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  metaValue: { color: '#222', fontSize: 14, marginTop: 4 },
  pair: { flexDirection: 'row', gap: 16 },
  pairItem: { flex: 1, gap: 8 },
  fieldLabel: { color: '#333', fontSize: 14, fontWeight: '600' },
  readonlyBox: {
    minHeight: FEEDBACK_BOX_MIN_HEIGHT,
    backgroundColor: '#F8F9FB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 12,
  },
  feedbackValue: { color: '#222', fontSize: 14 },
  fileList: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  fileTile: { width: 88, alignItems: 'center' },
  filePreview: {
    width: 88,
    height: 88,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#F8F9FB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileMark: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileBadge: {
    position: 'absolute',
    top: -6,
    right: -12,
    backgroundColor: '#4B5563',
    borderRadius: 3,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  fileBadgeText: { color: '#fff', fontSize: 9, fontWeight: '700' },
  fileName: {
    marginTop: 6,
    color: '#666',
    fontSize: 12,
    textAlign: 'center',
  },
  comment: {
    height: COMMENT_BOX_HEIGHT,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 12,
    textAlignVertical: 'top',
    color: '#222',
  },
  counter: { alignSelf: 'flex-end', color: '#888', fontSize: 12 },
  detailScroll: { flex: 1 },
  footer: {
    backgroundColor: '#fff',
    paddingHorizontal: 24,
    paddingVertical: 12,
    flexShrink: 0,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 3,
  },
  footerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  footerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  cancel: {
    borderWidth: 1,
    borderColor: '#666',
    borderRadius: 6,
    paddingHorizontal: 16,
    minHeight: 40,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelText: { color: '#666', fontSize: 14, fontWeight: '600' },
  save: {
    backgroundColor: colors.primary,
    borderRadius: 6,
    paddingHorizontal: 20,
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveText: { color: '#fff', fontSize: 14, fontWeight: '600' },
  saveError: { flex: 1, color: '#B91C1C', fontSize: 14 },
});
